/**
 * Quality: inspections and their checklists, NCRs, and snags.
 *
 * The verification record is shared between NCRs and snags — one table, one
 * rule (a different person has to check the fix), so both live here.
 */

import { ifMatch, request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';
// ------------------------------------------------------- inspections and NCRs

export type InspectionStatus =
  | 'requested'
  | 'scheduled'
  | 'in_progress'
  | 'passed'
  | 'passed_with_observations'
  | 'failed'
  | 'cancelled';

export type NcrStatus =
  | 'open'
  | 'corrective_action_assigned'
  | 'work_complete'
  | 'verified'
  | 'void';

export const CHECK_RESULTS = ['pass', 'fail', 'na'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

export interface ChecklistLine {
  readonly id: string;
  readonly sequence: number;
  readonly text: string;
  readonly result: CheckResult | null;
  readonly note: string | null;
}

export interface Inspection {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly status: InspectionStatus;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly requested_by_name: string | null;
  readonly requested_for_date: string | null;
  readonly inspector_id: string | null;
  readonly inspector_name: string | null;
  readonly scheduled_at: string | null;
  readonly conducted_at: string | null;
  readonly notes: string | null;
  readonly cancelled_reason: string | null;
  readonly lines: readonly ChecklistLine[];
  readonly unanswered: number;
  /** One is enough to fail the whole inspection. */
  readonly failed_lines: number;
  readonly ncr_numbers: readonly string[];
  /** Never contains a result — the checklist decides that. */
  readonly allowed_next: readonly InspectionStatus[];
  /**
   * Whether THIS caller may finish the walk.
   *
   * Separate because the field above never contains a verdict — they are
   * stripped for everybody — so it is empty on an inspection being walked
   * whether the reader is the inspector or somebody who may not touch it.
   */
  readonly can_conclude: boolean;
}

export interface NcrVerification {
  readonly id: string;
  readonly verifier_name: string | null;
  readonly outcome: string;
  readonly comment: string | null;
  readonly verified_at: string;
}

export interface Ncr {
  readonly id: string;
  readonly number: string;
  readonly description: string;
  readonly severity: Severity;
  readonly status: NcrStatus;
  readonly root_cause_category: string | null;
  readonly inspection_id: string | null;
  readonly inspection_number: string | null;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly corrective_action: string | null;
  readonly assignee_id: string | null;
  readonly assignee_name: string | null;
  readonly due_date: string | null;
  readonly completion_note: string | null;
  readonly raised_by_name: string | null;
  readonly raised_at: string;
  readonly closed_at: string | null;
  readonly void_reason: string | null;
  readonly days_open: number;
  /** Already excludes `verified` when the caller is the assignee. */
  readonly allowed_next: readonly NcrStatus[];
  readonly verifications: readonly NcrVerification[];
}

export interface QualityView {
  readonly inspections: readonly Inspection[];
  readonly ncrs: readonly Ncr[];
  /**
   * Two registers on one screen, so two totals. Each counts what THIS reader
   * may see. See `api/paging.ts`.
   */
  readonly inspections_total: number;
  readonly inspections_truncated: boolean;
  readonly ncrs_total: number;
  readonly ncrs_truncated: boolean;
}

const qualityPath = (projectId: string): string => `/v1/projects/${encodeURIComponent(projectId)}`;

export function listQuality(projectId: string, limit?: number): Promise<QualityView> {
  const query = limit === undefined ? '' : `?limit=${limit}`;
  return request<QualityView>(`${qualityPath(projectId)}/quality${query}`);
}

export function requestInspection(
  projectId: string,
  input: {
    title: string;
    location_id?: string | null;
    requested_for_date?: string | null;
    lines?: readonly string[];
  },
): Promise<QualityView> {
  return request<QualityView>(`${qualityPath(projectId)}/inspections`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateInspection(
  projectId: string,
  inspectionId: string,
  input: {
    inspector_id?: string | null;
    scheduled_for?: string | null;
    notes?: string | null;
    lines?: readonly string[];
  },
): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/inspections/${encodeURIComponent(inspectionId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export function answerLine(
  projectId: string,
  inspectionId: string,
  lineId: string,
  input: { result: CheckResult | null; note?: string | null },
): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/inspections/${encodeURIComponent(inspectionId)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

/**
 * Finish the walk. Takes NO result — a failed line is a failed inspection, and
 * the server computes the outcome from the checklist.
 */
export function concludeInspection(
  projectId: string,
  inspectionId: string,
  input: {
    observations?: string | null;
    ncr?: {
      description: string;
      severity?: Severity;
      root_cause_category?: string | null;
      assignee_id?: string | null;
      due_date?: string | null;
    };
  },
): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/inspections/${encodeURIComponent(inspectionId)}/conclude`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function moveInspection(
  projectId: string,
  inspectionId: string,
  input: { status: 'scheduled' | 'in_progress' | 'cancelled'; reason?: string },
): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/inspections/${encodeURIComponent(inspectionId)}/status`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export function deleteInspection(projectId: string, inspectionId: string): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/inspections/${encodeURIComponent(inspectionId)}`,
    { method: 'DELETE' },
  );
}

export function raiseNcr(
  projectId: string,
  input: {
    description: string;
    severity?: Severity;
    location_id?: string | null;
    assignee_id?: string | null;
    due_date?: string | null;
  },
): Promise<QualityView> {
  return request<QualityView>(`${qualityPath(projectId)}/ncrs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function moveNcr(
  projectId: string,
  ncrId: string,
  input: {
    status: NcrStatus;
    reason?: string;
    corrective_action?: string | null;
    assignee_id?: string | null;
    due_date?: string | null;
    completion_note?: string | null;
  },
): Promise<QualityView> {
  return request<QualityView>(
    `${qualityPath(projectId)}/ncrs/${encodeURIComponent(ncrId)}/status`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

// ----------------------------------------------------------------------- snags

export type SnagStatus =
  | 'open'
  | 'assigned'
  | 'in_progress'
  | 'closure_submitted'
  | 'verified_closed'
  | 'accepted_as_is'
  | 'void';

export const SNAG_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SNAG_SEVERITIES)[number];

export interface SnagVerification {
  readonly id: string;
  readonly verifier_name: string | null;
  readonly outcome: string;
  readonly comment: string | null;
  readonly verified_at: string;
}

export interface Snag {
  readonly id: string;
  /**
   * The concurrency token, opaque. Hand it back to `updateSnag` so a save on
   * top of somebody else's is refused rather than silently overwriting it.
   */
  readonly record_version: string;
  readonly number: string;
  readonly description: string;
  readonly severity: Severity;
  readonly status: SnagStatus;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly trade_id: string | null;
  readonly trade_name: string | null;
  readonly assignee_id: string | null;
  readonly assignee_name: string | null;
  readonly due_date: string | null;
  readonly raised_by_name: string | null;
  readonly raised_at: string;
  readonly closure_note: string | null;
  readonly closed_at: string | null;
  readonly accepted_as_is_reason: string | null;
  readonly void_reason: string | null;
  readonly days_open: number;
  readonly overdue: boolean;
  /** How many days this severity is allowed. */
  readonly age_threshold: number;
  /**
   * What THIS caller may move it to. Already excludes `verified_closed` when
   * the caller is the assignee — the SERVER decides that, not the browser.
   */
  readonly allowed_next: readonly SnagStatus[];
  readonly verifications: readonly SnagVerification[];
}

export interface SnagInput {
  readonly description?: string;
  readonly severity?: Severity;
  readonly location_id?: string | null;
  readonly trade_id?: string | null;
  readonly assignee_id?: string | null;
  readonly due_date?: string | null;
}

async function snagCall(path: string, init?: RequestInit): Promise<readonly Snag[]> {
  const body = await request<{ data: readonly Snag[] }>(path, init ?? {});
  return body.data;
}

const snagPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/snags`;

/**
 * The register, which the server bounds. See `api/paging.ts`.
 *
 * A subcontractor's total counts only what they may see — the scope is applied
 * before the count, not after.
 */
export function listSnags(projectId: string, limit?: number): Promise<RegisterPage<Snag>> {
  return pageCall<Snag>(snagPath(projectId), limit);
}

export function createSnag(
  projectId: string,
  input: SnagInput & { description: string },
): Promise<readonly Snag[]> {
  return snagCall(snagPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

export function updateSnag(
  projectId: string,
  snagId: string,
  input: SnagInput,
  /** The `record_version` this edit was started from. See `ifMatch`. */
  expectedVersion?: string,
): Promise<readonly Snag[]> {
  return snagCall(`${snagPath(projectId)}/${encodeURIComponent(snagId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
    ...ifMatch(expectedVersion),
  });
}

export function moveSnag(
  projectId: string,
  snagId: string,
  input: { status: SnagStatus; reason?: string; closure_note?: string | null },
): Promise<readonly Snag[]> {
  return snagCall(`${snagPath(projectId)}/${encodeURIComponent(snagId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteSnag(projectId: string, snagId: string): Promise<readonly Snag[]> {
  return snagCall(`${snagPath(projectId)}/${encodeURIComponent(snagId)}`, { method: 'DELETE' });
}

